import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { Phone, Mail } from "lucide-react";

const sections = [
  {
    title: "1. Sales and Warranty Policy",
    blocks: [
      {
        heading: "Product Coverage",
        text: "All computers sold by Gabicom LLC are covered by a 1-year manufacturer warranty.",
      },
      {
        heading: "Warranty Scope",
        text: "The manufacturer warranty applies to hardware defects and component failures under normal use. It does not cover:",
        list: [
          "Physical damage, liquid damage, or misuse.",
          "Unauthorized modifications or third-party repairs.",
          "Software issues unrelated to hardware defects.",
        ],
      },
      {
        heading: "Proof of Purchase",
        text: "Customers must provide a valid receipt or invoice from Gabicom LLC to claim warranty services.",
      },
    ],
  },
  {
    title: "2. Return and Refund Policy",
    blocks: [
      {
        heading: "30-Day Full Refund",
        text: "Customers may return computers within 30 calendar days of purchase for a full refund, provided that:",
        list: [
          "The product is in its original packaging with all included accessories.",
          "The computer shows no signs of damage, misuse, or unauthorized modification.",
        ],
      },
      {
        heading: "Condition Requirements",
        list: [
          "Items returned with missing parts or damaged packaging may be subject to a restocking fee.",
          "Software licenses and digital products are non-refundable once activated.",
        ],
      },
      {
        heading: "Refund Method",
        text: "Approved refunds will be issued to the original form of payment within 7–10 business days.",
      },
    ],
  },
  {
    title: "3. Repair Policy (Computers and Accessories)",
    blocks: [
      {
        heading: "Repair Services",
        text: "Gabicom LLC offers repair services for computers and accessories, both in-warranty (covered by manufacturer) and out-of-warranty (paid by customer).",
      },
      {
        heading: "Diagnostics",
        list: [
          "A diagnostic assessment fee may apply for non-warranty items.",
          "Customers will be provided with an estimate before repair work begins.",
        ],
      },
      {
        heading: "Warranty Repairs",
        list: [
          "Repairs under manufacturer warranty will be carried out in accordance with manufacturer guidelines.",
          "If the repair is not possible, replacement options will be discussed.",
        ],
      },
      {
        heading: "Out-of-Warranty Repairs",
        list: [
          "Customers are responsible for repair costs, including labor and parts.",
          "Gabicom LLC will provide a written quote, and customer approval is required before any work is done.",
        ],
      },
      {
        heading: "Repair Timeline",
        text: "Repair times vary based on part availability and issue severity. Customers will be updated regularly on status.",
      },
      {
        heading: "Service Guarantee",
        text: "All paid repairs carry a 90-day service warranty covering the specific repair performed.",
      },
    ],
  },
];

export default function TermsDialog({ children }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {children || (
          <button
            data-testid="footer-terms-link"
            className="text-sm text-[#a0aec0] transition-colors hover:text-white"
          >
            Terms &amp; Conditions
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        data-testid="terms-dialog"
        className="max-h-[85vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0d0e15] text-[#e2e8f0]"
      >
        <DialogHeader>
          <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00f0ff]">
            Gabicom LLC
          </span>
          <DialogTitle className="font-display text-2xl text-white">
            Terms &amp; Conditions
          </DialogTitle>
          <DialogDescription className="text-[#a0aec0]">
            Please review our sales, warranty, return and repair policies below.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-8">
          {sections.map((s) => (
            <div key={s.title}>
              <h3 className="font-display text-lg text-white">{s.title}</h3>
              <div className="mt-3 space-y-4">
                {s.blocks.map((b, i) => (
                  <div key={i}>
                    {b.heading && (
                      <p className="text-sm font-semibold text-[#c9d3e0]">{b.heading}</p>
                    )}
                    {b.text && (
                      <p className="mt-1 text-sm leading-relaxed text-[#a0aec0]">{b.text}</p>
                    )}
                    {b.list && (
                      <ul className="mt-2 space-y-1.5">
                        {b.list.map((item, j) => (
                          <li key={j} className="flex gap-2 text-sm leading-relaxed text-[#a0aec0]">
                            <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-[#b026ff]" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="rounded-2xl border border-white/10 bg-[#05050a] p-5">
            <h3 className="font-display text-lg text-white">4. Customer Support</h3>
            <p className="mt-2 text-sm text-[#a0aec0]">
              For warranty claims, returns, or repair inquiries, customers may contact
              Gabicom LLC – Customer Service:
            </p>
            <div className="mt-4 space-y-3">
              <a
                href="tel:+12023092266"
                data-testid="terms-phone"
                className="flex items-center gap-3 text-sm text-white transition-colors hover:text-[#00f0ff]"
              >
                <Phone className="h-4 w-4 text-[#00f0ff]" /> 1.202.309.2266
              </a>
              <a
                href="mailto:gabicom@gabicomitsolutions.com"
                data-testid="terms-email"
                className="flex items-center gap-3 text-sm text-white transition-colors hover:text-[#00f0ff]"
              >
                <Mail className="h-4 w-4 text-[#00f0ff]" /> gabicom@gabicomitsolutions.com
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
